import {
  Eye, KeyRound, MoreHorizontal, Plus, RefreshCw, Search, Server, Settings2, Trash2,
} from "lucide-react";
import * as React from "react";
import { Badge, StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Callout, EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import { CopyButton, Tooltip } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  MonoCell, Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { Page, PageHeader, Toolbar } from "@/components/layout/page";
import { EndpointFormDialog } from "@/features/endpoint-form";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  useDeleteEndpoint, useDomains, useEndpoints, useRevealPassword, useRotatePassword,
} from "@/lib/queries";
import { atLeast, type Endpoint, type EndpointSecret } from "@/lib/types";
import { useDebounced } from "@/lib/use-debounced";

export function EndpointsPage() {
  const { user: actor } = useAuth();
  const toast = useToast();
  const canManage = atLeast(actor?.role, "admin");

  const [search, setSearch] = React.useState("");
  const [domainId, setDomainId] = React.useState("all");
  const [assigned, setAssigned] = React.useState("all");
  const debounced = useDebounced(search, 250);

  const { data: domains } = useDomains();
  const { data, isPending, error, refetch, isFetching } = useEndpoints({
    q: debounced || undefined,
    domain_id: domainId === "all" ? undefined : Number(domainId),
    assigned: assigned === "all" ? undefined : assigned === "yes",
  });

  const [formOpen, setFormOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Endpoint | undefined>();
  const [deleting, setDeleting] = React.useState<Endpoint | undefined>();
  const [secret, setSecret] = React.useState<EndpointSecret | null>(null);
  const [rotating, setRotating] = React.useState<Endpoint | undefined>();

  const reveal = useRevealPassword();
  const rotate = useRotatePassword();
  const remove = useDeleteEndpoint();

  // `registered` is null for every row when the live-state store is unreachable — say so once
  // at the top rather than showing a column of ambiguous grey dots.
  const liveStateDown = Boolean(data?.length) && data!.every((e) => e.registered === null);
  const filtered = Boolean(debounced) || domainId !== "all" || assigned !== "all";

  return (
    <Page>
      <PageHeader
        title="Endpoints"
        description="Device registrations on the switch. A person's phone is made with their account and edited there; what you create here is a device nobody owns."
        action={
          <div className="flex gap-2">
            <Tooltip content="Refresh registration state">
              <Button
                variant="outline"
                size="icon"
                onClick={() => void refetch()}
                aria-label="Refresh"
              >
                <RefreshCw className={isFetching ? "animate-spin" : undefined} />
              </Button>
            </Tooltip>
            {canManage && (
              <Button
                variant="default"
                onClick={() => {
                  setEditing(undefined);
                  setFormOpen(true);
                }}
              >
                <Plus />
                New endpoint
              </Button>
            )}
          </div>
        }
      />

      {liveStateDown && (
        <Callout tone="warning" className="mb-4">
          Live registration state is unavailable, so every endpoint below shows as unknown rather
          than offline. The endpoints themselves are unaffected.
        </Callout>
      )}

      <Card className="overflow-hidden">
        <Toolbar>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search extensions"
            leading={<Search />}
            className="w-48"
            aria-label="Search endpoints"
          />
          <Select value={domainId} onValueChange={setDomainId}>
            <SelectTrigger className="w-48" aria-label="Filter by domain">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All domains</SelectItem>
              {(domains ?? []).map((d) => (
                <SelectItem key={d.id} value={String(d.id)}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={assigned} onValueChange={setAssigned}>
            <SelectTrigger className="w-40" aria-label="Filter by assignment">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any assignment</SelectItem>
              <SelectItem value="yes">Assigned</SelectItem>
              <SelectItem value="no">Unassigned</SelectItem>
            </SelectContent>
          </Select>
          {filtered && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearch("");
                setDomainId("all");
                setAssigned("all");
              }}
            >
              Clear
            </Button>
          )}
          <span className="ml-auto text-xs text-text-muted">
            {data ? `${data.length} endpoint${data.length === 1 ? "" : "s"}` : null}
          </span>
        </Toolbar>

        {isPending ? (
          <TableSkeleton cols={5} />
        ) : error ? (
          <ErrorState
            error={error}
            title="Could not load endpoints"
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        ) : data.length === 0 ? (
          <EmptyState
            icon={Server}
            title={filtered ? "No endpoints match those filters" : "No endpoints yet"}
            description={
              filtered
                ? "Try clearing the domain or assignment filter."
                : "Reps get theirs with their account. Create one here for a device that belongs to nobody — a shared handset, a door intercom."
            }
            action={
              canManage && !filtered ? (
                <Button variant="default" onClick={() => setFormOpen(true)}>
                  <Plus />
                  New endpoint
                </Button>
              ) : undefined
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Extension</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Assigned to</TableHead>
                <TableHead>Caller ID</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="pr-0">
                    <Tooltip
                      content={
                        e.registered === null
                          ? "Registration state unknown"
                          : e.registered
                            ? "Registered now"
                            : "Not registered"
                      }
                    >
                      <span className="inline-flex">
                        <StatusDot state={e.registered} />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <MonoCell>
                    <span className="inline-flex items-center gap-1">
                      <span className="text-text">{e.extension}</span>
                      <span className="text-text-subtle">@{e.domain_name}</span>
                      <CopyButton value={`${e.extension}@${e.domain_name}`} />
                    </span>
                    {!e.enabled && (
                      <Badge tone="warning" size="sm" className="ml-1.5 font-sans">
                        Disabled
                      </Badge>
                    )}
                    {e.number_alias && (
                      <Badge tone="outline" size="sm" className="ml-1.5">
                        dials {e.number_alias}
                      </Badge>
                    )}
                    {/* Only shown when recording is OFF. Every endpoint records by default, so
                        a badge on the normal case would be a column of noise — the exception is
                        what an operator is scanning for. */}
                    {!e.record_calls && (
                      <Badge tone="outline" size="sm" className="ml-1.5 font-sans">
                        Not recorded
                      </Badge>
                    )}
                  </MonoCell>
                  <TableCell className="text-text-muted">{e.display_name ?? "—"}</TableCell>
                  <TableCell>
                    {e.assigned_user_id ? (
                      <span className="truncate">{e.assigned_user_name}</span>
                    ) : (
                      <span className="text-xs text-text-subtle">Unassigned</span>
                    )}
                  </TableCell>
                  <MonoCell className="text-text-muted">{e.caller_id_number ?? "—"}</MonoCell>
                  <TableCell>
                    {canManage && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Actions for ${e.extension}`}
                          >
                            <MoreHorizontal />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem
                            onSelect={() => {
                              setEditing(e);
                              setFormOpen(true);
                            }}
                          >
                            <Settings2 />
                            Edit endpoint
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onSelect={async () => {
                              try {
                                setSecret(await reveal.mutateAsync(e.id));
                              } catch (err) {
                                toast.error(
                                  "Could not read the password",
                                  err instanceof ApiError ? err.message : undefined,
                                );
                              }
                            }}
                          >
                            <Eye />
                            Reveal password
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => setRotating(e)}>
                            <KeyRound />
                            Rotate password
                          </DropdownMenuItem>
                          {/* An owned endpoint goes when its account does — there is no
                              route that deletes one out from under its owner. */}
                          {!e.assigned_user_id && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem destructive onSelect={() => setDeleting(e)}>
                                <Trash2 />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <EndpointFormDialog open={formOpen} onOpenChange={setFormOpen} endpoint={editing} />

      <SecretDialog secret={secret} onClose={() => setSecret(null)} />

      <ConfirmDialog
        open={Boolean(rotating)}
        onOpenChange={(open) => !open && setRotating(undefined)}
        title="Rotate this SIP password?"
        description={
          <>
            A new password is generated for{" "}
            <span className="font-mono">
              {rotating?.extension}@{rotating?.domain_name}
            </span>
            . Any device using the old one stops registering until it is updated — including a
            rep's console phone the next time they reload.
          </>
        }
        confirmLabel="Rotate password"
        loading={rotate.isPending}
        onConfirm={async () => {
          if (!rotating) return;
          try {
            setSecret(await rotate.mutateAsync(rotating.id));
            setRotating(undefined);
          } catch (e) {
            toast.error("Could not rotate", e instanceof ApiError ? e.message : undefined);
          }
        }}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(undefined)}
        title="Delete this endpoint?"
        description={
          <>
            <span className="font-mono">
              {deleting?.extension}@{deleting?.domain_name}
            </span>{" "}
            will be removed from the switch directory and can no longer register. This cannot be
            undone.
          </>
        }
        confirmLabel="Delete endpoint"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await remove.mutateAsync(deleting.id);
            toast.success("Endpoint deleted", deleting.extension);
            setDeleting(undefined);
          } catch (e) {
            toast.error(
              "Could not delete",
              e instanceof ApiError ? e.message : "Please try again.",
            );
          }
        }}
      />
    </Page>
  );
}

/** Shows a SIP password once, with the warning that it is a credential. */
function SecretDialog({
  secret, onClose,
}: {
  secret: EndpointSecret | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={Boolean(secret)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>SIP password</DialogTitle>
          <DialogDescription>
            For{" "}
            <span className="font-mono">
              {secret?.extension}@{secret?.domain_name}
            </span>
            .
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <div className="flex items-center gap-2 rounded-md border border-border bg-surface-sunken px-3 py-2">
            <code className="min-w-0 flex-1 break-all font-mono text-sm text-text">
              {secret?.password}
            </code>
            <CopyButton value={secret?.password ?? ""} />
          </div>
          <Callout tone="warning">
            This is a live credential. Send it over something other than email, and rotate it if
            it goes anywhere it should not.
          </Callout>
        </DialogBody>
        <DialogFooter>
          <Button variant="default" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
